import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Activity, BarChart3, CalendarCheck2, CalendarDays, CheckCircle2,
    Clock3, Download, Filter, IdCard, RefreshCw, RotateCcw, TrendingUp,
    UserPlus, Users, XCircle,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import DatePicker from '../../components/DatePicker';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { CanvasDonutChart, CanvasHorizontalBars, CanvasLineChart } from './CanvasCharts';
import './Analytics.css';

const REFRESH_SECONDS = 30;
const CHART_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#f97316'];

const number = value => Number(value || 0).toLocaleString('en-IN');
const dateKey = date => {
    const d = new Date(date);
    const pad = value => String(value).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const daysAgo = days => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() - (days - 1));
    return dateKey(date);
};
const TODAY = dateKey(new Date());

const percentChange = (current, previous) => {
    if (!previous) return current ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
};

const StatCard = ({ label, value, detail, Icon, tone = 'primary' }) => (
    <article className={`analytics-stat analytics-stat--${tone}`}>
        <div className="analytics-stat__top">
            <div className="analytics-stat__icon"><Icon size={19} strokeWidth={2} /></div>
            <span className="analytics-stat__detail">{detail}</span>
        </div>
        <strong>{value}</strong>
        <span className="analytics-stat__label">{label}</span>
    </article>
);

const ChartHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="analytics-card__header">
        <div className="analytics-card__title-icon"><Icon size={17} /></div>
        <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
        </div>
    </div>
);

const EmptyState = ({ text }) => (
    <div className="analytics-empty"><BarChart3 size={23} /><span>{text}</span></div>
);

const Analytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState([]);
    const [acquisition, setAcquisition] = useState(null);
    const [startDate, setStartDate] = useState(daysAgo(14));
    const [endDate, setEndDate] = useState(TODAY);
    const [draftStartDate, setDraftStartDate] = useState(daysAgo(14));
    const [draftEndDate, setDraftEndDate] = useState(TODAY);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [nextRefresh, setNextRefresh] = useState(REFRESH_SECONDS);
    const mounted = useRef(true);
    const hasLoaded = useRef(false);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const [analyticsResponse, bookingsResponse, usersResponse, acquisitionResponse] = await Promise.all([
                API.get('/admin/analytics', { params: { startDate, endDate } }),
                API.get('/admin/bookings'),
                API.get('/admin/users'),
                API.get('/admin/acquisition', { params: { startDate, endDate } }).catch(() => null), // an older server has no such report: the rest still loads
            ]);
            if (!mounted.current) return;
            setAnalytics(analyticsResponse.data.analytics || {});
            setBookings(bookingsResponse.data.bookings || []);
            setUsers(usersResponse.data.users || []);
            setAcquisition(acquisitionResponse?.data || null);
            setLastUpdated(new Date());
            setNextRefresh(REFRESH_SECONDS);
            hasLoaded.current = true;
        } catch (error) {
            if (!hasLoaded.current || !silent) toast.error('Failed to refresh analytics');
        } finally {
            if (mounted.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [startDate, endDate]);

    useEffect(() => {
        mounted.current = true;
        fetchData(true);
        const refreshTimer = window.setInterval(() => {
            if (document.visibilityState === 'visible') fetchData(true);
        }, REFRESH_SECONDS * 1000);
        const countdownTimer = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                setNextRefresh(current => current > 1 ? current - 1 : REFRESH_SECONDS);
            }
        }, 1000);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') fetchData(true);
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            mounted.current = false;
            window.clearInterval(refreshTimer);
            window.clearInterval(countdownTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [fetchData]);

    const series = useMemo(() => (analytics?.dailySeries || []).map(item => ({
        label: item.label,
        date: item.date,
        members: item.users || 0,
        bookings: item.bookings || 0,
    })), [analytics]);

    const isInRange = useCallback(item => {
        if (!item?.createdAt) return false;
        const created = new Date(item.createdAt).toISOString().slice(0, 10);
        return created >= startDate && created <= endDate;
    }, [startDate, endDate]);

    const filteredUsers = useMemo(() => users.filter(isInRange), [users, isInRange]);
    const filteredBookings = useMemo(() => bookings.filter(isInRange), [bookings, isInRange]);

    const bookingStatus = useMemo(() => [
        { name: 'Confirmed', value: filteredBookings.filter(item => item.status === 'Confirmed').length, color: '#10b981' },
        { name: 'Pending', value: filteredBookings.filter(item => item.status === 'Pending').length, color: '#f59e0b' },
        { name: 'Cancelled', value: filteredBookings.filter(item => item.status === 'Cancelled').length, color: '#ef4444' },
    ], [filteredBookings]);

    const genderData = useMemo(() => (analytics?.genderStats || []).map(item => ({
        name: item._id === 'Female' ? 'Brides' : item._id === 'Male' ? 'Grooms' : (item._id || 'Other'),
        value: item.count || 0,
        color: item._id === 'Female' ? '#ec4899' : item._id === 'Male' ? '#0ea5e9' : '#8b5cf6',
    })), [analytics]);

    const religionData = useMemo(() => (analytics?.religionStats || [])
        .filter(item => item._id)
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
        .map((item, index) => ({ name: item._id, value: item.count || 0, color: CHART_COLORS[index % CHART_COLORS.length] })), [analytics]);

    const districtData = useMemo(() => (analytics?.districtStats || [])
        .filter(item => item._id)
        .map((item, index) => ({ name: item._id, value: item.count || 0, color: index === 0 ? 'primary' : '#8b5cf6' })), [analytics]);

    const confirmed = bookingStatus[0].value;
    const confirmationRate = filteredBookings.length ? Math.round((confirmed / filteredBookings.length) * 100) : 0;
    const periodUsers = analytics?.periodStats?.users ?? filteredUsers.length;
    const userTrend = percentChange(periodUsers, analytics?.periodStats?.previousUsers || 0);
    const rangeDays = Math.round((new Date(`${endDate}T12:00:00`) - new Date(`${startDate}T12:00:00`)) / 86400000) + 1;
    const rangeLabel = `${new Date(`${startDate}T12:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} – ${new Date(`${endDate}T12:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;

    const stats = [
        { label: 'Members joined', value: number(periodUsers), detail: `${userTrend >= 0 ? '+' : ''}${userTrend}% vs prior period`, Icon: Users, tone: 'primary' },
        { label: 'Active range', value: `${rangeDays}d`, detail: rangeLabel, Icon: UserPlus, tone: 'violet' },
        { label: 'New profiles', value: number(analytics?.periodStats?.profiles), detail: 'Created in range', Icon: IdCard, tone: 'blue' },
        { label: 'Total bookings', value: number(filteredBookings.length), detail: `${bookingStatus[1].value} awaiting action`, Icon: CalendarDays, tone: 'amber' },
        { label: 'Confirmed', value: number(confirmed), detail: `${confirmationRate}% confirmation rate`, Icon: CalendarCheck2, tone: 'green' },
    ];

    const applyDateFilter = () => {
        if (!draftStartDate || !draftEndDate) {
            toast.error('Choose both a start date and an end date');
            return;
        }
        if (draftStartDate > draftEndDate) {
            toast.error('Start date must be before the end date');
            return;
        }
        const days = Math.round((new Date(draftEndDate) - new Date(draftStartDate)) / 86400000) + 1;
        if (days > 366) {
            toast.error('Choose a date range of one year or less');
            return;
        }
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
    };

    const setQuickRange = days => {
        const nextStart = daysAgo(days);
        setDraftStartDate(nextStart);
        setDraftEndDate(TODAY);
        setStartDate(nextStart);
        setEndDate(TODAY);
    };

    const resetRange = () => setQuickRange(14);

    const exportReport = () => {
        const rows = [
            ['Analytics report', rangeLabel],
            ['Generated', new Date().toLocaleString('en-IN')],
            [],
            ['Metric', 'Value'],
            ['Members joined', periodUsers],
            ['Profiles created', analytics?.periodStats?.profiles || 0],
            ['Total bookings', filteredBookings.length],
            ...bookingStatus.map(item => [`Bookings - ${item.name}`, item.value]),
            [],
            ['Activity type', 'Created date', 'Name / reference', 'Email / service', 'Status'],
            ...filteredUsers.map(user => [
                'Member', new Date(user.createdAt).toLocaleString('en-IN'), user.name || 'Unnamed member', user.email || user.mobile || '', user.plan || 'Free',
            ]),
            ...filteredBookings.map(booking => [
                'Booking', new Date(booking.createdAt).toLocaleString('en-IN'), booking.user?.name || booking._id,
                booking.service?.businessName || booking.eventType || '', booking.status || '',
            ]),
        ];
        const csv = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-${startDate}-to-${endDate}.csv`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    return (
        <div className="analytics-layout">
            <Sidebar />
            <main className="admin-content analytics-content">
                <Navbar />
                <div className="analytics-page">
                    <header className="analytics-heading">
                        <div>
                            <div className="analytics-eyebrow"><Activity size={14} /> Live workspace</div>
                            <h1>Analytics overview</h1>
                            <p>Member growth, booking health and audience insights in one place.</p>
                        </div>
                        <div className="analytics-actions">
                            <div className="analytics-live" title={`Next refresh in ${nextRefresh} seconds`}>
                                <span className="analytics-live__dot" />
                                <span><b>Live</b>{lastUpdated ? ` · ${lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ' · Connecting'}</span>
                            </div>
                            <button className="analytics-button analytics-button--ghost" onClick={exportReport} disabled={!analytics}>
                                <Download size={16} /> Export
                            </button>
                            <button className="analytics-button analytics-button--primary" onClick={() => fetchData(false)} disabled={refreshing}>
                                <RefreshCw size={16} className={refreshing ? 'is-spinning' : ''} /> Refresh
                            </button>
                        </div>
                    </header>

                    <section className="analytics-filter" aria-label="Analytics date filter">
                        <div className="analytics-filter__title">
                            <div><Filter size={16} /></div>
                            <span><b>Date range</b><small>{rangeLabel}</small></span>
                        </div>
                        <div className="analytics-filter__quick" aria-label="Quick ranges">
                            {[7, 14, 30, 90].map(days => (
                                <button key={days} className={rangeDays === days ? 'is-active' : ''} onClick={() => setQuickRange(days)}>{days}D</button>
                            ))}
                        </div>
                        <div className="analytics-filter__dates">
                            <label><span>Start date</span><DatePicker value={draftStartDate} onChange={setDraftStartDate} placeholder="Start date" /></label>
                            <span className="analytics-filter__dash">to</span>
                            <label><span>End date</span><DatePicker value={draftEndDate} onChange={setDraftEndDate} placeholder="End date" /></label>
                        </div>
                        <button className="analytics-button analytics-button--ghost analytics-filter__reset" onClick={resetRange} title="Reset to 14 days"><RotateCcw size={15} /></button>
                        <button className="analytics-button analytics-button--primary" onClick={applyDateFilter} disabled={draftStartDate === startDate && draftEndDate === endDate}><Filter size={15} /> Apply</button>
                    </section>

                    {loading ? (
                        <div className="analytics-loading" aria-label="Loading analytics">
                            <BrandLoader size={60} />
                            <strong>Preparing your analytics</strong>
                            <span>Loading the latest member and booking activity…</span>
                        </div>
                    ) : (
                        <>
                            <section className="analytics-stats" aria-label="Key performance indicators">
                                {stats.map(stat => <StatCard key={stat.label} {...stat} />)}
                            </section>

                            <section className="analytics-grid analytics-grid--hero">
                                <article className="analytics-card analytics-card--wide">
                                    <div className="analytics-card__toolbar">
                                        <ChartHeader icon={TrendingUp} title="Growth activity" subtitle="New members and bookings over time" />
                                        <span className="analytics-range-label">{rangeDays} days</span>
                                    </div>
                                    <div className="analytics-chart-legend">
                                        <span><i className="analytics-legend-dot analytics-legend-dot--primary" /> Members</span>
                                        <span><i className="analytics-legend-dot analytics-legend-dot--violet" /> Bookings</span>
                                    </div>
                                    <CanvasLineChart data={series} className="analytics-canvas analytics-canvas--line" />
                                </article>

                                <article className="analytics-card">
                                    <ChartHeader icon={CalendarCheck2} title="Booking health" subtitle="Current status breakdown" />
                                    <CanvasDonutChart data={bookingStatus} centerLabel="Bookings" className="analytics-canvas analytics-canvas--donut" />
                                    <div className="analytics-donut-legend">
                                        {bookingStatus.map(item => (
                                            <div key={item.name}>
                                                <span><i style={{ background: item.color }} />{item.name}</span>
                                                <strong>{number(item.value)}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </article>
                            </section>

                            <section className="analytics-grid analytics-grid--details">
                                <article className="analytics-card">
                                    <ChartHeader icon={Users} title="Member mix" subtitle="Bride and groom distribution" />
                                    {genderData.some(item => item.value) ? (
                                        <>
                                            <CanvasDonutChart data={genderData} centerLabel="Members" className="analytics-canvas analytics-canvas--donut-small" />
                                            <div className="analytics-donut-legend analytics-donut-legend--compact">
                                                {genderData.map(item => (
                                                    <div key={item.name}>
                                                        <span><i style={{ background: item.color }} />{item.name}</span>
                                                        <strong>{number(item.value)}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : <EmptyState text="No member data yet" />}
                                </article>

                                <article className="analytics-card">
                                    <ChartHeader icon={BarChart3} title="Top districts" subtitle="Locations with the most profiles" />
                                    {districtData.length ? <CanvasHorizontalBars data={districtData} className="analytics-canvas analytics-canvas--bars" /> : <EmptyState text="No location data yet" />}
                                </article>

                                <article className="analytics-card">
                                    <ChartHeader icon={IdCard} title="Community insights" subtitle="Profiles grouped by religion" />
                                    {religionData.length ? <CanvasHorizontalBars data={religionData} className="analytics-canvas analytics-canvas--bars" /> : <EmptyState text="No profile data yet" />}
                                </article>
                            </section>

                            <section className="analytics-grid analytics-grid--single">
                                <article className="analytics-card">
                                    <ChartHeader icon={Filter} title="Sign-ups by source" subtitle={`New accounts in the selected dates and the link they came from (UTM). Premium = holds a plan now.`} />
                                    {acquisition?.sources?.length ? (
                                        <div className="analytics-source-wrap">
                                            <table className="analytics-source-table">
                                                <thead><tr><th>Source</th><th>Medium</th><th>Campaign</th><th>Sign-ups</th><th>Premium</th><th>Premium %</th></tr></thead>
                                                <tbody>
                                                    {acquisition.sources.map((row, index) => (
                                                        <tr key={`${row.source}-${row.medium}-${row.campaign}-${index}`}>
                                                            <td data-label="Source"><b>{row.source}</b></td>
                                                            <td data-label="Medium">{row.medium || '-'}</td>
                                                            <td data-label="Campaign">{row.campaign || '-'}</td>
                                                            <td data-label="Sign-ups">{number(row.signups)}</td>
                                                            <td data-label="Premium">{number(row.premium)}</td>
                                                            <td data-label="Premium %">{row.signups ? Math.round((row.premium / row.signups) * 100) : 0}%</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot><tr><td colSpan={3}>Total</td><td data-label="Total sign-ups">{number(acquisition.totalSignups)}</td><td data-label="Total premium">{number(acquisition.totalPremium)}</td><td data-label="Premium %">{acquisition.totalSignups ? Math.round((acquisition.totalPremium / acquisition.totalSignups) * 100) : 0}%</td></tr></tfoot>
                                            </table>
                                        </div>
                                    ) : <EmptyState text="No sign-ups in these dates yet" />}
                                </article>
                            </section>

                            <section className="analytics-status-strip">
                                <div><CheckCircle2 size={17} /><span><b>{confirmed}</b> confirmed bookings</span></div>
                                <div><Clock3 size={17} /><span><b>{bookingStatus[1].value}</b> pending follow-ups</span></div>
                                <div><XCircle size={17} /><span><b>{bookingStatus[2].value}</b> cancellations</span></div>
                                <p>Charts update automatically every {REFRESH_SECONDS} seconds · next update in {nextRefresh}s</p>
                            </section>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Analytics;
